import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';

interface Props {
  eventoId: number;
}

interface HistoricoLinha {
  quando: string;
  quem: string;
  o_que: string;
  detalhe: string | null;
}

const CARD = 'rounded-3xl bg-white p-4 shadow-[0_6px_18px_rgba(20,25,60,.06)]';

function formatarQuando(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Histórico do que foi feito no evento.
export default function BlocoHistorico({ eventoId }: Props) {
  const [linhas, setLinhas] = useState<HistoricoLinha[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let ativo = true;
    (async () => {
      setCarregando(true);
      setErro(null);
      try {
        const { data, error } = await supabase.rpc('historico_do_evento', {
          p_evento: eventoId,
          p_limite: 50,
        });
        if (error) throw error;
        if (ativo) setLinhas((data as HistoricoLinha[]) || []);
      } catch (e) {
        if (ativo) setErro(mensagemAmigavel(e, 'Não foi possível carregar o histórico.'));
      } finally {
        if (ativo) setCarregando(false);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [eventoId]);

  if (carregando) {
    return (
      <div className="space-y-2">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-14 animate-pulse rounded-2xl bg-secondary-100" />
        ))}
      </div>
    );
  }

  if (erro) {
    return (
      <div className="rounded-2xl bg-danger-50 px-4 py-3 text-[14px] text-danger-700">{erro}</div>
    );
  }

  return (
    <div className={CARD}>
      {linhas.length === 0 ? (
        <p className="text-[14px] text-foreground-500">Nenhuma atividade ainda.</p>
      ) : (
        <ul>
          {linhas.map((h, i) => (
            <li key={i} className="flex min-h-[52px] items-start justify-between gap-3 py-2">
              <div className="min-w-0">
                <p className="text-[14px] text-foreground-800">
                  <strong className="font-semibold">{h.quem}</strong> {h.o_que}
                </p>
                {h.detalhe && (
                  <p className="truncate text-[13px] text-foreground-500">{h.detalhe}</p>
                )}
              </div>
              <span className="shrink-0 whitespace-nowrap text-[12px] text-foreground-400">
                {formatarQuando(h.quando)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}